import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BiDownload } from "react-icons/bi";
import { FiEdit2 } from "react-icons/fi";

import DeleteButton from "../../../../components/DeleteButton/DeleteButton";
import Button from "../../../../components/Utils/Button/Button";
import DynamicTable from "../../../../components/Utils/DynamicTable/DynamicTable";
import useLoadingStore from "../../../../stores/LoadingStore";
import useUserStore from "../../../../stores/UserStore";
import AccountingRevenueType from "../../../../types/AccountingRevenue";
import { formatCurrency } from "../../../../Utils/formatCurrency";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

const RevenueList: React.FC<{
  onEdit: (revenue: AccountingRevenueType) => void;
  onEditPage?: boolean;
  search: string;
  startDate: Date;
  endDate: Date;
}> = ({ onEdit, onEditPage, search, startDate, endDate }) => {
  const [revenue, setRevenue] = useState<AccountingRevenueType[]>();
  const { loadingStates, setLoading } = useLoadingStore();
  const { isVatSubject } = useUserStore();
  const [sortState, setSortState] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const revenuesData = await appFetch(
        "/api/accounting/revenue/getRevenues",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString(),
          }),
        },
      );
      if (revenuesData) {
        setRevenue(revenuesData.revenues);
      }
    };

    fetchData();
  }, [endDate, startDate]);

  const handleSort = (column: keyof AccountingRevenueType | "event.name") => {
    const direction = sortState?.direction === "asc" ? "desc" : "asc";
    setSortState({ column, direction });
    setRevenue((prevRevenues) => {
      if (!prevRevenues) return prevRevenues;
      return prevRevenues.slice().sort((a, b) => {
        let aValue: any;
        let bValue: any;
        if (column === "event.name") {
          aValue = a.event?.name;
          bValue = b.event?.name;
        } else if (column === "unit_price" || column === "tva_rate") {
          aValue = Number(a[column] ?? 0);
          bValue = Number(b[column] ?? 0);
        } else {
          aValue = a[column];
          bValue = b[column];
        }
        if (aValue == null || bValue == null) return 0;
        if (aValue < bValue) return direction === "asc" ? -1 : 1;
        if (aValue > bValue) return direction === "asc" ? 1 : -1;
        return 0;
      });
    });
  };

  const filteredRevenues = revenue?.filter((item) => {
    return (
      item.product_name.toLowerCase().includes(search.toLowerCase()) ||
      item.event?.name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const downloadRevenue = async (item: AccountingRevenueType) => {
    setLoading(`downloadRevenue${item.revenue_id}`, true);
    try {
      const fileUrl = `/api/accounting/revenue/getRevenuesFile?revenue_id=${item.revenue_id}`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = item.product_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Erreur lors du téléchargement du fichier");
    }
    setLoading(`downloadRevenue${item.revenue_id}`, false);
  };

  const deleteRevenue = async (revenueId: number) => {
    setLoading(`deleteRevenue${revenueId}`, true);
    const response = await appFetch(
      `/api/accounting/revenue/deleteRevenue?revenue_id=${revenueId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ revenue_id: revenueId }),
      },
    );

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      toast.success(response.message);

      setRevenue((prev) => {
        if (prev) {
          return prev.filter((item) => item.revenue_id !== revenueId);
        }
        return prev;
      });

      document.getElementById("cancelModalButton")?.click();
    }
    setLoading(`deleteRevenue${revenueId}`, false);
  };

  let columns = [
    {
      title: "Nom",
      dataIndex: "product_name",
      sortable: true,
      onSort: () => handleSort("product_name"),
      sortableClass:
        sortState?.column === "product_name" ? sortState.direction : undefined,
    },
    {
      title: "Date",
      dataIndex: "recorded_date",
      render: (value: string) => {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        });
      },
      sortable: true,
      onSort: () => handleSort("recorded_date"),
      sortableClass:
        sortState?.column === "recorded_date" ? sortState.direction : undefined,
    },
    {
      title: "Mode de paiement",
      dataIndex: "payment_method",
      sortable: true,
      onSort: () => handleSort("payment_method"),
      sortableClass:
        sortState?.column === "payment_method"
          ? sortState.direction
          : undefined,
    },
    {
      title: "Event",
      dataIndex: "event",
      render: (value: { name: string }) => {
        return value.name;
      },
      sortable: true,
      onSort: () => handleSort("event.name"),
      sortableClass:
        sortState?.column === "event.name" ? sortState.direction : undefined,
    },
    {
      title: "Prix unitaire",
      dataIndex: "unit_price",
      render: (value: number) => {
        return `${formatCurrency(value)} €`;
      },
      sortable: true,
      onSort: () => handleSort("unit_price"),
      sortableClass:
        sortState?.column === "unit_price" ? sortState.direction : undefined,
    },
    {
      title: "Quantité",
      dataIndex: "amount",
      sortable: true,
      onSort: () => handleSort("amount"),
      sortableClass:
        sortState?.column === "amount" ? sortState.direction : undefined,
    },
    ...(isVatSubject
      ? [
          {
            title: "TVA",
            dataIndex: "tva_rate",
            render: (value: number) => {
              return `${value ?? 0}%`;
            },
            sortable: true,
            onSort: () => handleSort("tva_rate"),
            sortableClass:
              sortState?.column === "tva_rate"
                ? sortState.direction
                : undefined,
          },
        ]
      : []),
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: string, record: AccountingRevenueType) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            className="downloadButton"
            onClick={() => downloadRevenue(record)}
            icon={<BiDownload />}
            isLoading={loadingStates[`downloadRevenue${record.revenue_id}`]}
            style="square"
          />
          <Button
            className="editButton"
            onClick={() => onEdit(record)}
            icon={<FiEdit2 />}
            style="square"
          />
          <DeleteButton
            onClick={() => deleteRevenue(record.revenue_id)}
            isLoading={loadingStates[`deleteRevenue${record.revenue_id}`]}
          />
        </div>
      ),
    },
  ];

  if (onEditPage) {
    columns = columns.filter((column) => column.title !== "Actions");
  }

  const handleRowClick = (item: AccountingRevenueType) => {
    if (onEditPage) {
      onEdit(item);
    }
  };

  return (
    <div className={`accountingListPage ${onEditPage ? "onEditPage" : ""}`}>
      <div className="tableContainer">
        <DynamicTable
          data={filteredRevenues ?? []}
          columns={columns}
          onClick={handleRowClick}
          pagined
          maxRow={25}
          mobile
          renderSummary={(visibleRevenues) => {
            const total = visibleRevenues.reduce(
              (sum: number, item: AccountingRevenueType) =>
                sum + Number(item.unit_price ?? 0) * Number(item.amount ?? 0),
              0,
            );

            return `Montant total: ${formatCurrency(total)} €`;
          }}
        />
      </div>
    </div>
  );
};

export default RevenueList;
